import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import {
    AuthScreen,
    AuthBody,
    AuthFooter,
    ScreenHeader,
    TextField,
    PrimaryButton,
    GhostButton,
} from "@/screens/auth/_ui";

const Login = () => {
    const navigate = useNavigate();
    const { signIn } = useAuth();
    const [identifier, setIdentifier] = useState("");
    const [password, setPassword] = useState("");
    const [showPw, setShowPw] = useState(false);
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);

    const handleSubmit = () => {
        const e = {};
        if (!identifier.trim()) e.identifier = "Required";
        if (!password) e.password = "Required";
        if (Object.keys(e).length) {
            setErrors(e);
            return;
        }
        setErrors({});
        setSubmitting(true);
        // Tiny delay so button press feels natural (pure UX, no network)
        setTimeout(async () => {
            let res;
            try {
                res = await signIn(identifier, password);
            } catch {
                setSubmitting(false);
                setErrors({ form: "Could not complete request. Please try again." });
                return;
            }
            setSubmitting(false);
            if (!res.ok) {
                // Sanitize technical errors before surfacing.
                let msg = res.error || "Incorrect login details";
                if (/json|body stream|fetch|network/i.test(msg)) {
                    msg = "Could not complete request. Please try again.";
                }
                setErrors({ form: msg });
                return;
            }
            navigate("/home", { replace: true });
        }, 250);
    };

    return (
        <AuthScreen testId="login-screen">
            <ScreenHeader title="Log in" />
            <AuthBody>
                <div className="pt-4 space-y-1 animate-fade-up">
                    <h2 className="font-display text-[24px] font-semibold tracking-tight">
                        Welcome back
                    </h2>
                    <p className="text-[14px] text-muted-foreground">
                        Log in with your username, email or phone.
                    </p>
                </div>

                <div className="mt-8 space-y-4 animate-fade-up" style={{ animationDelay: "80ms" }}>
                    <TextField
                        testId="login-identifier-input"
                        label="Username, email or phone"
                        placeholder="e.g. ada, ada@mail.com, +234…"
                        value={identifier}
                        onChange={setIdentifier}
                        autoComplete="username"
                        autoFocus
                        error={errors.identifier}
                    />
                    <TextField
                        testId="login-password-input"
                        label="Password"
                        placeholder="Your password"
                        type={showPw ? "text" : "password"}
                        value={password}
                        onChange={setPassword}
                        autoComplete="current-password"
                        error={errors.password}
                        right={
                            <button
                                type="button"
                                data-testid="login-toggle-password"
                                onClick={() => setShowPw((s) => !s)}
                                className="h-9 w-9 flex items-center justify-center rounded-lg text-muted-foreground hover:text-foreground"
                            >
                                {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
                            </button>
                        }
                    />

                    {errors.form && (
                        <div
                            data-testid="login-form-error"
                            className="rounded-xl border border-destructive/40 bg-destructive/10 text-destructive text-[13px] font-medium px-4 py-3"
                        >
                            {errors.form}
                        </div>
                    )}

                    <div className="flex justify-end">
                        <button
                            data-testid="login-forgot-link"
                            onClick={() => navigate("/auth/forgot")}
                            className="text-[13px] font-medium text-primary hover:underline"
                        >
                            Forgot password?
                        </button>
                    </div>
                </div>
            </AuthBody>

            <AuthFooter>
                <PrimaryButton
                    testId="login-submit-btn"
                    onClick={handleSubmit}
                    loading={submitting}
                >
                    Log in
                </PrimaryButton>
                <GhostButton
                    testId="login-go-signup"
                    onClick={() => navigate("/auth/signup")}
                >
                    Don't have an account? Sign up
                </GhostButton>
            </AuthFooter>
        </AuthScreen>
    );
};

export default Login;
